import { SupportedStyles } from '../Table';

const UNKNOWN_DEFAULT = { en: 'Unknown', ja: '不明' };

export type SupportedLanguages = 'en' | 'ja';

const formatKnownObjectRequestValue = (
  language: SupportedLanguages,
  type: string,
  value: RequestDateTime
) => {
  switch (type) {
    case 'dateTimeWithoutMatching':
    case 'requestDateTime': {
      if (value.flexibility === 'flexible') {
        return language === 'ja' ? '日時は相談可能' : 'flexible';
      } else if (value.flexibility === 'other') {
        return value.flexibilityOther as string;
      } else if (value.date) {
        return value.date.split('T')[0];
      }
    }
  }
  return undefined;
};

const formatValueForTable = (
  language: SupportedLanguages,
  type: string,
  value: RequestField['value']
): string => {
  switch (typeof value) {
    case 'object': {
      if (Array.isArray(value)) {
        return value
          .map((valueElement) => {
            const valueInLocale = valueElement[language];
            return valueInLocale;
          })
          .filter(Boolean)
          .join('\n');
      }
      const formattedValue = formatKnownObjectRequestValue(language, type, value);
      if (formattedValue) {
        return formattedValue;
      }
      break;
    }
    case 'number': {
      return `${value}`;
    }
    case 'string': {
      return value;
    }
  }
  return UNKNOWN_DEFAULT[language];
};

const getFieldLabel = (
  language: 'en' | 'ja',
  label: string | { en?: string; ja?: string }
): string => {
  let rowLabel;
  if (typeof label === 'object') {
    rowLabel = label[language] ?? UNKNOWN_DEFAULT[language];
  } else {
    rowLabel = label.length > 0 ? label : UNKNOWN_DEFAULT[language];
  }
  const suffixCharacterForLocale = language === 'ja' ? '：' : ':';
  return rowLabel.endsWith(suffixCharacterForLocale)
    ? rowLabel
    : `${rowLabel}${suffixCharacterForLocale}`;
};

interface RequestDateTime {
  date?: string;
  flexibility: 'flexible' | 'specificDate' | 'other';
  flexibilityOther?: string;
  type?: 'afternoon' | 'anyTime' | 'morning' | 'timeRange' | 'evening';
}

export interface RequestField {
  key: string;
  label: { en?: string; ja?: string } | string;
  type: string;
  value: Array<{ en?: string; ja?: string }> | RequestDateTime | number | string;
}

export const createTableRows = (
  requestFields: Array<RequestField>,
  language: SupportedLanguages
) => {
  const tableRows = requestFields.map(({ key, label, type, value }) => {
    const fieldValue = formatValueForTable(language, type, value);
    const fieldLabel = getFieldLabel(language, label);
    return {
      fieldLabel,
      fieldValue,
      id: key,
      verticalAlign: 'top' as SupportedStyles['verticalAlign'],
    };
  });
  return tableRows;
};
